import { useEffect, useState, useMemo, useRef } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  STATUS_LABELS, STATUS_COLORS, PETITION_TYPES, PETITION_STATUSES, formatDate
} from "@/lib/constants";
import { Plus, Edit, Trash2, Upload, FileText as FileIcon, X, ExternalLink } from "lucide-react";
import { useQuickAdd } from "@/lib/useQuickAdd";
import { downloadFile } from "@/lib/download";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function emptyP() {
  return {
    applicant_name: "", village_id: "", subject: "", petition_type: "dilekce",
    date: new Date().toISOString().slice(0, 10),
    status: "beklemede", result_note: "", file_ids: [],
  };
}

export default function PetitionsPage() {
  const [items, setItems] = useState([]);
  const [villages, setVillages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyP());
  const [uploadedFiles, setUploadedFiles] = useState([]); // {id, filename}
  const [removing, setRemoving] = useState(null);

  const removeFile = async () => {
    const f = removing;
    setRemoving(null);
    try {
      await api.delete(`/files/${f.id}`);
      setUploadedFiles((list) => list.filter((x) => x.id !== f.id));
      toast.success("Ek kalıcı olarak silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Ek silinemedi");
    }
  };
  const [uploading, setUploading] = useState(false);
  const [fileNames, setFileNames] = useState({});
  const fileInputRef = useRef(null);

  const villageMap = useMemo(() => Object.fromEntries(villages.map((v) => [v.id, v.name])), [villages]);

  const load = () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (typeFilter !== "all") params.petition_type = typeFilter;
    api.get("/petitions", { params }).then((r) => {
      setItems(r.data);
      const ids = r.data.flatMap((p) => p.file_ids || []);
      if (ids.length) {
        api.post("/files/info", ids).then((res) =>
          setFileNames(Object.fromEntries(res.data.map((f) => [f.id, f.filename]))));
      }
    });
  };

  useEffect(() => { api.get("/villages").then((r) => setVillages(r.data)); }, []);
  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const openNew = () => { setEditing(null); setForm(emptyP()); setUploadedFiles([]); setOpen(true); };
  const quickDone = useQuickAdd((vid) => { setEditing(null); setForm({ ...emptyP(), village_id: vid }); setUploadedFiles([]); setOpen(true); }, open);
  const openEdit = (p) => {
    setEditing(p); setForm({ ...p });
    setUploadedFiles((p.file_ids || []).map((id) => ({ id, filename: fileNames[id] || "Dosya" })));
    setOpen(true);
  };

  const save = async () => {
    if (!form.applicant_name || !form.subject || !form.village_id) return toast.error("Dilekçe sahibi, köy ve konu zorunludur");
    const body = { ...form, file_ids: uploadedFiles.map((f) => f.id) };
    try {
      if (editing) await api.put(`/petitions/${editing.id}`, body);
      else await api.post("/petitions", body);
      toast.success(editing ? "Güncellendi" : "Oluşturuldu");
      setOpen(false); load();
      if (!editing) quickDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (p) => {
    if (!window.confirm("Dilekçe silinsin mi?")) return;
    await api.delete(`/petitions/${p.id}`);
    toast.success("Silindi"); load();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedFiles([...uploadedFiles, { id: r.data.id, filename: r.data.filename }]);
      toast.success("Dosya yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dosya yüklenemedi");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openFile = async (fid) => {
    try {
      await downloadFile(`/files/${fid}/download`, "dosya");
    } catch (e) {
      toast.error("Dosya indirilemedi");
    }
  };

  return (
    <AppLayout
      title="Dilekçe ve Talep Takibi"
      subtitle="Muhtar ve vatandaş dilekçeleri, arıza bildirimleri, etüt projeleri"
      actions={
        <Button onClick={openNew} data-testid="new-petition-button" className="bg-emerald-700 hover:bg-emerald-800">
          <Plus className="h-4 w-4 mr-1.5" /> Yeni Dilekçe
        </Button>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="max-w-full overflow-x-auto">
          <TabsList data-testid="petition-status-tabs">
            <TabsTrigger value="all">Tümü</TabsTrigger>
            {PETITION_STATUSES.map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`petition-status-tab-${s}`}>
                {STATUS_LABELS[s]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48" data-testid="petition-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Türler</SelectItem>
            {Object.entries(PETITION_TYPES).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        <Table data-testid="petitions-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Tarih</TableHead>
              <TableHead>Dilekçe Sahibi</TableHead>
              <TableHead>Köy</TableHead>
              <TableHead>Tür</TableHead>
              <TableHead>Konu</TableHead>
              <TableHead>Dosya</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead className="w-24">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="row-hover">
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">
                Kayıt bulunmuyor.
              </TableCell></TableRow>
            )}
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`petition-row-${p.id}`}>
                <TableCell className="font-mono text-xs whitespace-nowrap">{formatDate(p.date)}</TableCell>
                <TableCell className="font-medium">{p.applicant_name}</TableCell>
                <TableCell>{villageMap[p.village_id] || "-"}</TableCell>
                <TableCell><Badge variant="outline">{PETITION_TYPES[p.petition_type]}</Badge></TableCell>
                <TableCell className="max-w-[280px] truncate">{p.subject}</TableCell>
                <TableCell>
                  {p.file_ids?.length ? (
                    <div className="flex gap-1">
                      {p.file_ids.slice(0, 3).map((fid) => (
                        <Button key={fid} variant="ghost" size="icon" className="h-7 w-7"
                                title={fileNames[fid] || "Dosya"}
                                onClick={() => openFile(fid)}
                                data-testid={`view-file-${fid}`}>
                          <FileIcon className="h-3.5 w-3.5 text-blue-600" />
                        </Button>
                      ))}
                      {p.file_ids.length > 3 && <span className="text-xs text-slate-500 self-center">+{p.file_ids.length - 3}</span>}
                    </div>
                  ) : "-"}
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[p.status]}`}>
                    {STATUS_LABELS[p.status]}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)}
                            data-testid={`edit-petition-${p.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p)}
                            data-testid={`delete-petition-${p.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="petition-dialog">
          <DialogHeader><DialogTitle>{editing ? "Dilekçeyi Düzenle" : "Yeni Dilekçe / Talep"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Dilekçe Sahibi *</Label>
              <Input value={form.applicant_name}
                     onChange={(e) => setForm({ ...form, applicant_name: e.target.value })}
                     data-testid="petition-applicant-input" />
            </div>
            <div>
              <Label>Köy *</Label>
              <Select value={form.village_id} onValueChange={(v) => setForm({ ...form, village_id: v })}>
                <SelectTrigger data-testid="petition-village-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {villages.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tür</Label>
              <Select value={form.petition_type} onValueChange={(v) => setForm({ ...form, petition_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PETITION_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tarih</Label>
              <DateField value={form.date} className="w-full" clearable={false} testId="petition-date-input"
                     onChange={(v) => setForm({ ...form, date: v })} />
            </div>
            <div className="col-span-2">
              <Label>Konu *</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                     data-testid="petition-subject-input" />
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="petition-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PETITION_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div></div>
            <div className="col-span-2">
              <Label>Sonuç Notu</Label>
              <Textarea value={form.result_note}
                        onChange={(e) => setForm({ ...form, result_note: e.target.value })}
                        data-testid="petition-result-note-input" />
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label>Belgeler (PDF / Fotoğraf)</Label>
                <input type="file" ref={fileInputRef} className="hidden"
                       accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" onChange={handleFileSelect}
                       data-testid="petition-file-input" />
                <Button type="button" variant="outline" size="sm" disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="petition-upload-button">
                  <Upload className="h-3 w-3 mr-1" /> {uploading ? "Yükleniyor..." : "Dosya Yükle"}
                </Button>
              </div>
              <div className="space-y-1.5">
                {uploadedFiles.length === 0 && (
                  <div className="text-xs text-slate-400 py-2">Henüz dosya yüklenmedi.</div>
                )}
                {uploadedFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs"
                       data-testid={`uploaded-file-${f.id}`}>
                    <FileIcon className="h-3.5 w-3.5 text-blue-600" />
                    <span className="flex-1 truncate">{f.filename}</span>
                    <button type="button" onClick={() => openFile(f.id)} className="text-blue-600 hover:underline">
                      <ExternalLink className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => setRemoving(f)} aria-label="Eki sil" title="Eki sil"
                            className="p-1 -m-1 rounded text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                            data-testid={`remove-file-${f.id}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="petition-submit-button">
              {editing ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!removing} onOpenChange={(o) => { if (!o) setRemoving(null); }}>
        <AlertDialogContent data-testid="remove-file-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ek silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              "{removing?.filename}" dilekçeden çıkarılacak ve depodan kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-file-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={removeFile} className="bg-rose-700 hover:bg-rose-800" data-testid="remove-file-confirm">Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
